import { booleanAttribute, ChangeDetectionStrategy, Component, OnInit, input, output } from '@angular/core';
import {
  Bucket,
  EditableSplitTransactionChild,
  EditableTransaction,
  Transaction,
  UpdateTransactionPayload,
} from '../../types';
import { CurrencyPipe } from '@angular/common';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiCurrencyInputComponent } from '../../ui/currency-input/currency-input.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SelectBucketTypeaheadComponent } from '../../shared/select-bucket-typeahead/select-bucket-typeahead.component';
import { UiButtonDirective } from '../../ui/button/button.directive';

@Component({
  selector: 'app-split-transaction',
  templateUrl: './split-transaction.component.html',
  styleUrl: './split-transaction.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiFieldComponent,
    UiCurrencyInputComponent,
    ReactiveFormsModule,
    FormsModule,
    SelectBucketTypeaheadComponent,
    UiButtonDirective,
    CurrencyPipe,
  ],
})
export class SplitTransactionComponent implements OnInit {
  readonly splitTransaction = output<UpdateTransactionPayload | boolean>();
  readonly transaction = input.required<EditableTransaction>();
  readonly transactionChange = output<EditableTransaction>();
  readonly buckets = input<Bucket[]>([]);
  readonly embedded = input(false, { transform: booleanAttribute });

  constructor() {}

  ngOnInit() {
    const transaction = this.transaction();
    if (!transaction.children) transaction.children = transaction.parent ? transaction.parent.children : [];
    if (transaction.children.length < 1) this.addSplit();
  }

  get originalTransactionAmount() {
    const transaction = this.transaction();
    return transaction.parent ? transaction.parent.amount : transaction.amount;
  }

  get parentTransaction(): Transaction | undefined {
    return this.transaction().parent;
  }

  get children(): Array<Transaction | EditableSplitTransactionChild> {
    const transactionValue = this.transaction();
    if (!transactionValue.children) {
      const { children: _children, showSplit: _showSplit, ...transaction } = transactionValue;
      // Split children use '' for an unset bucket; the payload allows null for clears.
      return [{ ...transaction, bucketId: transaction.bucketId ?? '' }];
    }
    return transactionValue.children;
  }

  saveSplit() {
    const children = this.children.map(child => {
      const transactionChild = { ...child };
      delete transactionChild.parent;
      return transactionChild;
    });
    const transaction = this.transaction();
    transaction.showSplit = false;
    if (transaction.parent) {
      if (children.length === 1) {
        const newParent: UpdateTransactionPayload = {
          ...transaction.parent,
          ...children[0],
          id: transaction.parent.id,
        };
        return this.splitTransaction.emit(newParent);
      }
      return this.splitTransaction.emit({
        ...transaction.parent,
        children,
      });
    }
    return this.splitTransaction.emit({
      ...transaction,
      children,
    });
  }

  cancelSplit() {
    this.transaction().showSplit = false;
  }

  collapseSplit() {
    this.transaction().showSplit = false;
  }

  get totalAmount(): number {
    return this.children.reduce((acc, curr) => parseFloat((curr.amount + acc).toFixed(2)), 0);
  }

  get diff(): number {
    return this.roundNumber(this.totalAmount - this.originalTransactionAmount);
  }

  addSplit() {
    const { children: _children, id: _id, showSplit: _showSplit, ...draft } = this.transaction();
    const newChild = { ...draft, bucketId: draft.bucketId ?? '' };
    if (this.totalAmount < this.originalTransactionAmount)
      newChild.amount = this.roundNumber(this.originalTransactionAmount - this.totalAmount);
    else if (this.children.length > 0) newChild.amount = 0;
    this.ensureTransactionChildren().push(newChild);
  }

  deleteSplit(i: number) {
    const children = this.ensureTransactionChildren();
    children.splice(i, 1);
    if (children.length === 1) this.collapseToRemainingChild(children[0]);
  }

  roundNumber(num: number) {
    return Math.round(num * 100) / 100;
  }

  getBucket(id: string) {
    return this.buckets().find(bucket => bucket.id === id);
  }

  updateChildBucket(child: Transaction | EditableSplitTransactionChild, bucket: Bucket | undefined): void {
    child.bucketId = bucket?.id ?? '';
  }

  private ensureTransactionChildren(): Array<Transaction | EditableSplitTransactionChild> {
    const transaction = this.transaction();
    if (!transaction.children) transaction.children = [];
    return transaction.children;
  }

  private collapseToRemainingChild(child: Transaction | EditableSplitTransactionChild | undefined): void {
    if (!child) return;

    const transaction = this.transaction();
    const collapsedChild = {
      ...child,
      amount: this.originalTransactionAmount,
    };

    if (collapsedChild.id) transaction.children = [collapsedChild];
    else {
      const { id: _id, parent: _parent, ...childDraft } = collapsedChild as EditableSplitTransactionChild;
      Object.assign(transaction, childDraft);
      delete transaction.children;
    }

    transaction.showSplit = false;
    this.transactionChange.emit(transaction);
  }
}
